import { useEffect } from "react";

const SITE = "https://comfortcarenemt.com";

/**
 * Centralized SEO component for SPA pages.
 * Sets <title>, meta description, OG/Twitter tags, canonical URL, and JSON-LD schema.
 *
 * Usage:
 *   <Seo
 *     title="Page Title | Comfort Care"
 *     description="..."
 *     path="/services/dialysis-transportation"
 *     image="https://..."
 *     keywords={["...", "..."]}
 *     jsonLd={{ "@context": "https://schema.org", ... }}
 *   />
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  keywords,
  jsonLd,
  noIndex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;
    const canonical = `${SITE}${path}`;

    const upsertMeta = (key, attr, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Standard
    upsertMeta("description", "name", description);
    if (keywords?.length) upsertMeta("keywords", "name", keywords.join(", "));
    upsertMeta("robots", "name", noIndex ? "noindex, nofollow" : "index, follow");
    upsertMeta("author", "name", "Comfort Care Transportation");

    // Open Graph
    upsertMeta("og:title", "property", title);
    upsertMeta("og:description", "property", description);
    upsertMeta("og:url", "property", canonical);
    upsertMeta("og:type", "property", "website");
    upsertMeta("og:site_name", "property", "Comfort Care Transportation");
    if (image) upsertMeta("og:image", "property", image);

    // Twitter
    upsertMeta("twitter:card", "name", "summary_large_image");
    upsertMeta("twitter:title", "name", title);
    upsertMeta("twitter:description", "name", description);
    if (image) upsertMeta("twitter:image", "name", image);

    // Canonical link
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // JSON-LD schema (replace per-page)
    const scriptId = "ccnemt-page-schema";
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, image, keywords, jsonLd, noIndex]);

  return null;
}

// Reusable LocalBusiness schema fragment — used on home + every city page
export const LOCAL_BUSINESS_SCHEMA = {
  "@type": "MedicalBusiness",
  "@id": "https://comfortcarenemt.com/#business",
  name: "Comfort Care Transportation",
  alternateName: "Comfort Care Transportation NEMT",
  description:
    "Non-emergency medical transportation (NEMT) serving Virginia, Washington DC, and Maryland. Wheelchair accessible, dialysis transport, hospital discharge, senior NEMT.",
  image:
    "https://customer-assets.emergentagent.com/job_eb2dcda5-bc04-4ae1-9816-1e732eb67750/artifacts/u7xt03tz_CC-Transport-Logo-2-1920w%20%281%29.webp",
  logo:
    "https://customer-assets.emergentagent.com/job_eb2dcda5-bc04-4ae1-9816-1e732eb67750/artifacts/u7xt03tz_CC-Transport-Logo-2-1920w%20%281%29.webp",
  url: "https://comfortcarenemt.com/",
  telephone: "+15716894470",
  email: "info@comfortcarenemt.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "4209 Evergreen Lane, Suite #202",
    addressLocality: "Annandale",
    addressRegion: "VA",
    postalCode: "22003",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 38.8304,
    longitude: -77.1964,
  },
  areaServed: [
    { "@type": "State", name: "Virginia" },
    { "@type": "City", name: "Washington, D.C." },
    { "@type": "State", name: "Maryland" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  ],
  sameAs: [
    "https://share.google/3lA4MV1JxIb3TqtLT",
    "https://www.google.com/maps/place/Comfort+Care+Transportation+NEMT/data=!4m2!3m1!1s0x0:0x3c0c10707cc78985",
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    bestRating: "5",
    worstRating: "1",
    ratingCount: "1",
  },
};
